/**
 * DispositionVehicleCard — Juni-2026 (Refactor aus DispositionPage.jsx).
 *
 * Eine Fahrzeug-Zeile in der Disposition-Liste inkl. Auswahl-Checkbox
 * (Gruppenbearbeitung), Status-Badge, Zähl-Badges, Käufer-/Transport-Infos
 * und den Zeilen-Aktionen (Dispo öffnen, Auktion, Preisschild/QR, Verkaufen,
 * Ladenhüter-Schnellaktion).
 *
 * Props:
 *   item               – Datensatz aus /api/dealer/disposition/search
 *   selected           – ob die Zeile für die Gruppenbearbeitung ausgewählt ist
 *   onToggleSelect(id) – schaltet die Auswahl um
 *   onLadenhueterAction(item) – öffnet die Ladenhüter-Schnellaktion
 */
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import {
    ArrowRight, Hash, MapPin, Truck, Mail, Phone, Package, AlertTriangle,
    ShoppingCart, TrendingDown, Printer, ClipboardCheck, QrCode, Car, Gavel, KeyRound, Clock, Store, Eye, Handshake, CheckCircle2, Zap, ShieldAlert, X, Archive,
} from "lucide-react";
import api, { formatPrice, formatMileage, formatEzDate } from "../../lib/api";
import { toast } from "sonner";
import { Camera as CameraIcon } from "lucide-react";
import DispoPhotoCaptureModal from "./DispoPhotoCaptureModal";
import { getDmsSettings, dmsUrlForVin } from "../../lib/dms";
import { formatDeliveryWeek } from "../../lib/deliveryWeek";
import DispoRowNote from "../listing/DispoRowNote";
import OrderTrackingCard, { ORDER_STAGES } from "./OrderTrackingCard";
import { computeTuev, tuevLabel } from "../../lib/tuev";

const _fmtD = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } };

/** Jul-2026 (User-Wunsch) — gelber Hinweis + Direktfeld zum FIN-Nachtrag. */
const VinQuickFix = ({ item, isForeign }) => {
    const [open, setOpen] = useState(false);
    const [vin, setVin] = useState("");
    const [busy, setBusy] = useState(false);
    const [savedVin, setSavedVin] = useState(null);
    if (savedVin) {
        return <span className="font-mono-num inline-flex items-center gap-0.5 text-emerald-700" data-testid={`vin-quickfix-saved-${item.id}`}><Hash className="w-3 h-3" />VIN {savedVin}</span>;
    }
    const save = async () => {
        const v = vin.trim();
        if (v.length < 5) { toast.error("FIN zu kurz (mind. 5 Zeichen)"); return; }
        setBusy(true);
        try {
            const r = await api.patch(`/listings/${item.id}/dispo/vin`, { vin: v });
            setSavedVin(r.data.vin);
            toast.success(`FIN ${r.data.vin} gespeichert`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "FIN konnte nicht gespeichert werden");
        } finally { setBusy(false); }
    };
    return (
        <span className="inline-flex items-center gap-1 flex-wrap" data-testid={`vin-quickfix-${item.id}`}>
            <span className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-700 border-amber-300"
                title="Diesem Fahrzeug fehlt die Fahrgestellnummer">
                <AlertTriangle className="w-3 h-3" /> FIN fehlt
            </span>
            {!isForeign && !open && (
                <button type="button" onClick={(e) => { e.preventDefault(); setOpen(true); }}
                    className="text-[10px] font-bold underline underline-offset-2 text-amber-700 hover:text-amber-900"
                    data-testid={`vin-quickfix-open-${item.id}`}>
                    jetzt nachtragen
                </button>
            )}
            {!isForeign && open && (
                <>
                    <input value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
                        placeholder="Fahrgestellnummer"
                        className="h-6 w-44 border border-amber-400 rounded-sm px-1.5 text-[11px] font-mono-num bg-white"
                        data-testid={`vin-quickfix-input-${item.id}`} autoFocus />
                    <button type="button" onClick={save} disabled={busy}
                        className="h-6 px-2 rounded-sm bg-[#111827] text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-60"
                        data-testid={`vin-quickfix-save-${item.id}`}>
                        {busy ? "…" : "Speichern"}
                    </button>
                    <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X className="w-3 h-3" /></button>
                </>
            )}
        </span>
    );
};
const _fmtDT = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };

/**
 * Jun-2026 (User-Wunsch) — Reservierungs-Countdown-Badge in der Dispo-Liste.
 * Farbcode nach Dringlichkeit: <6h rot, <24h gelb, sonst grün; abgelaufen = rot.
 * Tickt minütlich (Listen-Overview braucht keine Sekundengenauigkeit).
 */
function ReservationCountdownBadge({ until, variant = "badge" }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(t);
    }, []);
    if (!until) return null;
    const ms = new Date(until).getTime() - now;
    if (Number.isNaN(ms)) return null;
    const fmt = (m) => {
        const tot = Math.max(0, Math.floor(m / 60000));
        const d = Math.floor(tot / 1440);
        const h = Math.floor((tot % 1440) / 60);
        const mi = tot % 60;
        if (d > 0) return `${d}T ${h}Std`;
        if (h > 0) return `${h}Std ${mi}Min`;
        return `${mi}Min`;
    };
    // Kompakte Variante fürs Bild-Overlay (nur die größte Einheit).
    const fmtShort = (m) => {
        const tot = Math.max(0, Math.floor(m / 60000));
        const d = Math.floor(tot / 1440);
        const h = Math.floor((tot % 1440) / 60);
        const mi = tot % 60;
        if (d > 0) return `${d}T`;
        if (h > 0) return `${h}Std`;
        return `${mi}Min`;
    };
    const expired = ms <= 0;
    const hrs = ms / 3600000;

    if (variant === "overlay") {
        // Jun-2026 (User-Wunsch) — klein & dezent unten aufs Fahrzeugbild gelegt.
        // Nov-2026 (User-Wunsch): unter 24h ROT.
        const oCls = expired || hrs < 24
            ? "bg-red-600/90 text-white"
            : "bg-black/65 text-white";
        return (
            <span
                className={`absolute bottom-0 inset-x-0 inline-flex items-center justify-center gap-0.5 px-0.5 py-px text-[8px] font-bold leading-none backdrop-blur-[1px] ${oCls}`}
                title={`Läuft ab am ${_fmtDT(until)} Uhr — danach gibt das System das Fahrzeug automatisch wieder frei`}
                data-testid="disposition-row-reservation-countdown-overlay"
            >
                <Clock className="w-2 h-2 shrink-0" /> {expired ? "abgelaufen" : fmtShort(ms)}
            </span>
        );
    }

    let cls;
    let txt;
    if (expired) {
        cls = "bg-red-600 text-white border-red-700";
        txt = "Löschfrist abgelaufen";
    } else {
        // Nov-2026 (User-Wunsch): unter 24h ROT (gut sichtbar), sonst grün.
        cls = hrs < 24
            ? "bg-red-600 text-white border-red-700"
            : "bg-emerald-100 text-emerald-700 border-emerald-400";
        txt = `Löschung in ${fmt(ms)}`;
    }
    return (
        <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border rounded-sm text-[9px] font-bold uppercase tracking-wider ${cls}`}
            title={`Löschfrist läuft ab am ${_fmtDT(until)} Uhr — danach löscht das System den Vorgang automatisch und gibt das Fahrzeug wieder frei`}
            data-testid="disposition-row-reservation-countdown"
        >
            <Clock className="w-2.5 h-2.5" /> {txt}
        </span>
    );
}

const STATUS_BADGES = {
    // Jul-2026 (User-Vorgabe): verfügbar=grün, vorläufig verkauft=gelb,
    // reserviert=orange, verkauft=rot.
    verfügbar: "bg-green-700 text-white border-green-800",
    "vorläufig verkauft": "bg-yellow-400 text-[#111827] border-yellow-500",
    reserviert: "bg-orange-500 text-white border-orange-600",
    verkauft: "bg-red-600 text-white border-red-700",
};

const LastCountBadge = ({ label, iso, testid, showUncounted = false }) => (
    iso ? (
        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-300" data-testid={testid} title={`${label} zuletzt gezählt: ${_fmtDT(iso)}`}>
            <ClipboardCheck className="w-3 h-3" /> {label} {_fmtD(iso)}
        </span>
    ) : (showUncounted ? (
        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-700 border-amber-300" data-testid={testid} title={`${label}: noch nie gezählt`}>
            <ClipboardCheck className="w-3 h-3" /> {label} nie gezählt
        </span>
    ) : null)
);

/**
 * Abmelde-Warnindikator (HU/Versicherungs-Check) — Schwellwert-Stufe für ein
 * ISO-Datum: null (fehlt), "ok" (<6 M), "warn" (6–12 M), "danger" (>12 M).
 */
function deregistrationLevel(isoDate) {
    if (!isoDate) return null;
    try {
        const parts = String(isoDate).split("-");
        const y = Number(parts[0]);
        const m = Number(parts[1] || 1);
        const d = Number(parts[2] || 1);
        if (!y) return null;
        const then = new Date(Date.UTC(y, m - 1, d));
        const now = new Date();
        const months = (now.getUTCFullYear() - then.getUTCFullYear()) * 12
                        + (now.getUTCMonth() - then.getUTCMonth());
        if (months < 6) return "ok";
        if (months <= 12) return "warn";
        return "danger";
    } catch {
        return null;
    }
}

function DeregistrationBadge({ isoDate, vehicle }) {
    const level = deregistrationLevel(isoDate);
    if (!level || level === "ok") return null;
    // Jul-2026 (User-Vorgabe): bis 50 ccm / 45 km/h gibt es keinen TÜV —
    // daher auch kein „TÜV prüfen"-Hinweis bei langer Abmeldung.
    const exempt = vehicle && computeTuev(vehicle)?.level === "exempt";
    if (exempt) return null;
    const isDanger = level === "danger";
    const cls = isDanger
        ? "bg-red-100 text-red-800 border-red-400"
        : "bg-orange-100 text-orange-800 border-orange-400";
    const txt = isDanger ? "TÜV prüfen" : "Abmeldung > 6 Mon.";
    return (
        <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border rounded-sm text-[9px] font-bold uppercase tracking-wider ${cls}`}
            title={`Letzte Abmeldung: ${isoDate} — ${isDanger ? "HU vermutlich abgelaufen, Versicherung erloschen, Neuzulassung erforderlich" : "TÜV/Versicherung vor Übergabe checken"}`}
            data-testid={`disposition-row-deregistered-${level}`}
        >
            <AlertTriangle className="w-2.5 h-2.5" />
            {txt}
        </span>
    );
}

// Jun-2026 (User-Wunsch) — Zustand-Kennzeichnung je Fahrzeug (NW/TZ/VFW/GW).
const CONDITION_BADGE = {
    NW: { label: "Neufahrzeug", cls: "bg-emerald-600 text-white border-emerald-700" },
    TZ: { label: "Tageszulassung", cls: "bg-sky-100 text-sky-800 border-sky-300" },
    VFW: { label: "Vorführfahrzeug", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    GW: { label: "Gebrauchtfahrzeug", cls: "bg-violet-100 text-violet-800 border-violet-300" },
};
const conditionCode = (c) => {
    if (!c) return null;
    if (["Neufahrzeug", "Neu", "Neuwagen", "new"].includes(c)) return "NW";
    if (c === "Tageszulassung") return "TZ";
    if (["Vorführfahrzeug", "Vorführwagen", "Vorführ"].includes(c)) return "VFW";
    if (["Gebrauchtfahrzeug", "Gebrauchtwagen", "Gebraucht", "used"].includes(c)) return "GW";
    return null;
};
function ConditionBadge({ item: it }) {
    const code = conditionCode(it.condition);
    const m = code && CONDITION_BADGE[code];
    if (!m) return null;
    return (
        <span
            className={`inline-flex items-center px-1.5 py-0.5 border rounded-sm text-[9px] font-bold uppercase tracking-wider ${m.cls}`}
            title={`Zustand: ${m.label}`}
            data-testid={`disposition-row-condition-${it.id}`}
        >
            {code}
        </span>
    );
}


function VorfuehrUsageBadge({ item: it }) {
    // Jun-2026 (User-Wunsch) — Nur bei zugelassenem Vorführfahrzeug mit gewählter Nutzung.
    if (it.deregistered || it.condition !== "Vorführfahrzeug" || !it.vorfuehr_usage) return null;
    const map = {
        dienstwagen: { label: "Dienstwagen", detail: it.vorfuehr_employee_name },
        miet_werkstatt: { label: "Miet/Werkstatt", detail: it.vorfuehr_workshop_site_name },
        anderes: { label: "Nutzung", detail: it.vorfuehr_other_text },
    };
    const m = map[it.vorfuehr_usage];
    if (!m) return null;
    const text = m.detail ? `${m.label}: ${m.detail}` : m.label;
    return (
        <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border rounded-sm text-[9px] font-bold uppercase tracking-wider bg-[#FFCC00]/20 text-[#111827] border-[#111827]/30 max-w-[200px] truncate"
            title={`Vorführfahrzeug — aktuelle Nutzung: ${text}`}
            data-testid={`disposition-row-vorfuehr-${it.id}`}
        >
            <Car className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">{text}</span>
        </span>
    );
}

function ActiveUsageBadges({ item: it }) {
    // Jun-2026 (User-Wunsch) — Hinweis, wenn das Fahrzeug aktuell in einer
    // aktiven Auktion läuft ODER vermietet/reserviert ist (Werkstattersatz-/
    // Dienstwagen). Beide Zustände können gleichzeitig sichtbar sein.
    const badges = [];
    if (it.active_auction) {
        const auctionId = it.active_auction_listing_id || it.id;
        badges.push(
            <Link
                key="auction"
                to={`/listing/${auctionId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border rounded-sm text-[9px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800 border-purple-400 hover:bg-purple-200 hover:border-purple-600 transition-colors"
                title="Zur laufenden Auktion springen"
                data-testid={`disposition-row-active-auction-${it.id}`}
            >
                <Gavel className="w-2.5 h-2.5" /> Aktive Auktion <ArrowRight className="w-2.5 h-2.5" />
            </Link>,
        );
    }
    if (it.active_rental && it.active_rental.status) {
        const r = it.active_rental;
        const isRented = r.status === "vermietet";
        const cls = isRented
            ? "bg-rose-100 text-rose-800 border-rose-400 hover:bg-rose-200 hover:border-rose-600"
            : "bg-amber-100 text-amber-800 border-amber-400 hover:bg-amber-200 hover:border-amber-600";
        const lbl = isRented ? "Vermietet" : "Miete reserviert";
        const detail = [r.customer_name, r.to ? `bis ${_fmtD(r.to)}` : null].filter(Boolean).join(" · ");
        badges.push(
            <Link
                key="rental"
                to={`/dealer/workshop-orders?tab=rental${r.booking_id ? `&focus=${r.booking_id}` : ""}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border rounded-sm text-[9px] font-bold uppercase tracking-wider max-w-[220px] truncate transition-colors ${cls}`}
                title={`Werkstattersatz-/Mietwagen — ${lbl}${detail ? `: ${detail}` : ""} · zur Mietbuchung springen`}
                data-testid={`disposition-row-active-rental-${it.id}`}
            >
                <KeyRound className="w-2.5 h-2.5 shrink-0" />
                <span className="truncate">{detail ? `${lbl}: ${detail}` : lbl}</span>
                <ArrowRight className="w-2.5 h-2.5 shrink-0" />
            </Link>,
        );
    }
    if (!badges.length) return null;
    return <>{badges}</>;
}

export default function DispositionVehicleCard({ item: it, currentStandort, selected, onToggleSelect, onLadenhueterAction, onCreateOffer, onTestDrive, onForeignReserve, onForeignDeal, onCreateTransport, onCreateDelivery, onSofortmitnahme }) {
    // Jul-2026 (User-Wunsch) — B2B-Preis nur für Führung/Disposition sichtbar:
    // Inhaber (b2b-Konto), Admin, Filialleiter, Verkaufsleiter, Geschäftsführer,
    // Gebietsleiter, Disposition. Normale Mitarbeiter sehen ihn nicht.
    const { user: authUser } = useAuth();
    // Aug-2026 (User-Wunsch): DMS-Basis-URL aus den Dispo-Einstellungen (gecacht)
    const [dmsBase, setDmsBase] = useState("");
    useEffect(() => {
        if (!it.vin) return;
        getDmsSettings().then((s) => { if (s?.base_url) setDmsBase(s.base_url); });
    }, [it.vin]);
    const _fn = (authUser?.employee_function || "").toLowerCase();
    const canSeeB2BPrice =
        authUser?.role === "b2b" || authUser?.role === "admin" ||
        ["filialleiter", "verkaufsleiter", "geschaeftsfuehrer", "gebietsleiter", "disposition", "inhaber"].includes(_fn);
    const badgeCls = STATUS_BADGES[it.status_label] || STATUS_BADGES.verfügbar;
    // Jun-2026 (User-Wunsch) — Scope „Alle Händler": fremde Fahrzeuge sind nur
    // zur Ansicht (keine eigenen Dispo-Aktionen), aber Kauf/Reservierung/
    // Vermittlung sind über die Inserat-Detailseite möglich.
    const isForeign = it.foreign === true;
    // Jul-2026 (User-Wunsch) — fremdes Fahrzeug, das als Kommissionsware an
    // MEINEM Standort steht → volle Dispo-Funktionen; von mir verkauft/vermittelt
    // → zusätzlich Auftrag & Transport.
    const foreignFull = isForeign && it.foreign_at_my_location === true;
    const foreignSold = isForeign && it.foreign_sold_by_me === true;
    const isOrderVehicle = it.stock_state === "bestell" || !!it.order_tracking;
    // Jun-2026 (Bugfix, wiederhergestellt): Verkauft/Reserviert blendet Verkaufs-Aktionen aus.
    const saleBlocked = it.status_label === "verkauft" || it.status_label === "reserviert" || it.sold === true;
    // Nov-2026 (User-Wunsch) — sichtbarer 72h-Countdown auf der Fahrzeugkarte:
    // reserviert → reserved_until (Fallback hold_expires_at), vorläufig verkauft
    // → hold_expires_at (Zeitpunkt der automatischen System-Freigabe).
    const holdUntil = it.status_label === "reserviert"
        ? (it.reserved_until || it.hold_expires_at)
        : it.status_label === "vorläufig verkauft" ? it.hold_expires_at : null;
    const [showTracking, setShowTracking] = useState(false);
    const [showPhotoCapture, setShowPhotoCapture] = useState(false); // Jul-2026: Foto-Button
    return (
        <li
            className={`relative bg-white border rounded-sm p-3 flex items-start gap-3 hover:shadow-sm transition-all ${isForeign ? "border-l-4 border-l-blue-500 border-gray-200 hover:border-blue-400" : selected ? "border-[#FFCC00] ring-2 ring-[#FFCC00]/40" : "border-gray-200 hover:border-[#FFCC00]"}`}
            data-testid={`disposition-row-${it.id}`}
        >
            {/* Jul-2026 (User-Wunsch) — Ankreuz-Feld bei JEDEM Fahrzeug, immer oben rechts. */}
            <label
                className="absolute top-1.5 right-1.5 z-10 inline-flex items-center justify-center w-6 h-6 rounded-sm bg-white border border-gray-300 shadow-sm cursor-pointer hover:border-[#FFCC00]"
                title="Fahrzeug für Gruppenbearbeitung auswählen"
                data-testid={`disposition-row-select-wrap-${it.id}`}
            >
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelect(it.id)}
                    className="w-4 h-4 accent-[#FFCC00] cursor-pointer"
                    aria-label="Fahrzeug für Gruppenbearbeitung auswählen"
                    data-testid={`disposition-row-select-${it.id}`}
                />
            </label>
            <Link
                to={`/listing/${it.id}`}
                className="relative w-16 h-16 shrink-0 bg-gray-100 rounded-sm overflow-hidden block"
                aria-label={`Inserat ansehen: ${it.title || it.brand || "Fahrzeug"}`}
                target="_blank" rel="noopener noreferrer">
                {it.image ? (
                    <img src={it.image} alt={it.title || "Fahrzeugbild"} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Package className="w-6 h-6" />
                    </div>
                )}
                {/* Jun-2026 (User-Wunsch) — Countdown klein & dezent aufs Bild gelegt.
                    Nov-2026: auch bei „vorläufig verkauft" (72h-Auto-Freigabe). */}
                {holdUntil && (
                    <ReservationCountdownBadge until={holdUntil} variant="overlay" />
                )}
            </Link>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap pr-7">
                    <ConditionBadge item={it} />
                    <Link to={`/listing/${it.id}`} className="font-bold text-sm text-[#111827] hover:underline truncate" target="_blank" rel="noopener noreferrer">
                        {it.title || "Fahrzeug"}
                    </Link>
                    {it.status_label !== "reserviert" && it.status_label !== "verkauft" && (
                        <span className={`text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border ${badgeCls}`}>
                            {it.status_label}
                        </span>
                    )}
                    {(it.internal_number || it.internal_vehicle_number) && (
                        <span className="text-[10px] font-mono-num font-bold px-1.5 py-0.5 rounded-sm bg-[#111827] text-[#FFCC00]" data-testid={`disposition-row-number-${it.id}`} title="Angebots-/Fahrzeugnummer">
                            {it.internal_number || it.internal_vehicle_number}
                        </span>
                    )}
                    {/* Jun-2026 (User-Wunsch) — externe Wagennummer rechts neben der
                        internen Angebotsnummer. */}
                    {it.wagennummer && (
                        <span className="text-[10px] font-mono-num font-bold px-1.5 py-0.5 rounded-sm bg-gray-200 text-gray-700 border border-gray-300" data-testid={`disposition-row-wagennummer-${it.id}`} title="Wagennummer (extern)">
                            WG {it.wagennummer}
                        </span>
                    )}
                    {/* Jul-2026 (User-Wunsch) — Foto-Vollständigkeit: erledigte Pflicht-Fotos (8 Slots). */}
                    {!isForeign && typeof it.photo_slots_done === "number" && (
                        <button type="button" onClick={() => setShowPhotoCapture(true)}
                            className={`inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border transition-colors ${it.photo_slots_done >= 8 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : it.photo_slots_done > 0 ? "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}
                            data-testid={`disposition-row-photos-${it.id}`}
                            title={it.photo_slots_done >= 8 ? "Alle 8 Pflicht-Fotos vorhanden" : `Noch ${8 - it.photo_slots_done} von 8 Pflicht-Fotos fehlen — klicken zum Aufnehmen (gesamt ${it.photos_total || 0} Bilder)`}>
                            <CameraIcon className="w-2.5 h-2.5" /> {it.photo_slots_done}/8 Fotos
                        </button>
                    )}
                    {/* Jun-2026 (User-Wunsch) — „Reserviert"/„Verkauft" rechts neben der
                        Fahrzeugnummer anzeigen (gut sichtbarer Status-Pin). */}
                    {it.status_label === "reserviert" && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm bg-orange-500 text-white border border-orange-600" data-testid={`disposition-row-state-reserved-${it.id}`} title="Fahrzeug ist reserviert">
                            <Clock className="w-2.5 h-2.5" /> Reserviert
                        </span>
                    )}
                    {/* Nov-2026 (User-Wunsch) — sichtbarer 72h-Countdown neben dem Status. */}
                    {holdUntil && <ReservationCountdownBadge until={holdUntil} />}
                    {it.status_label === "verkauft" && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm bg-red-600 text-white border border-red-700" data-testid={`disposition-row-state-sold-${it.id}`} title="Fahrzeug ist verkauft">
                            <CheckCircle2 className="w-2.5 h-2.5" /> Verkauft
                        </span>
                    )}
                    {it.is_leasing_vehicle && (
                        <Link to={`/dealer/leasingrueckgabe?q=${encodeURIComponent(it.vin || "")}`}
                            className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm bg-indigo-600 text-white border border-indigo-700 hover:bg-indigo-700 transition-colors" data-testid={`disposition-row-leasing-${it.id}`} title={`Leasingfahrzeug — Klick öffnet den Vorgang im Rückgabeportal${it.leasing_company ? ` · ${it.leasing_company}` : ""}`}>
                            <KeyRound className="w-2.5 h-2.5" /> Leasing{it.leasing_end_date ? ` bis ${it.leasing_end_date.slice(8, 10)}.${it.leasing_end_date.slice(5, 7)}.${it.leasing_end_date.slice(2, 4)}` : ""}
                        </Link>
                    )}
                    {/* Jun-2026 (User-Wunsch) — Finanzierungs-Badge (laufender Vertrag, wie Leasing) */}
                    {it.is_financing_vehicle && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500 text-black border border-amber-600" data-testid={`disposition-row-financing-${it.id}`} title="Laufender Finanzierungsvertrag">
                            <KeyRound className="w-2.5 h-2.5" /> Finanzierung{it.financing_end_date ? ` bis ${it.financing_end_date.slice(8, 10)}.${it.financing_end_date.slice(5, 7)}.${it.financing_end_date.slice(2, 4)}` : ""}
                        </span>
                    )}
                    {/* Jul-2026 (User-Wunsch) — TÜV-Warnung: fehlend/abgelaufen/unter 3 Monate. */}
                    {(() => { const t = it.status_label !== "verkauft" ? computeTuev(it) : null; return t && t.level !== "ok" && t.level !== "exempt" ? (
                        <span className={`inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border ${t.level === "overdue" ? "bg-red-600 text-white border-red-700" : "bg-amber-500 text-white border-amber-600"}`} data-testid={`disposition-row-tuev-${it.id}`} title={tuevLabel(t)}>
                            <ShieldAlert className="w-2.5 h-2.5" /> {t.level === "unknown" ? "TÜV prüfen" : t.level === "overdue" ? "TÜV abgelaufen" : `TÜV ${t.dueDate.toLocaleDateString("de-DE", { month: "2-digit", year: "numeric" })}`}{t.estimated ? " ~" : ""}
                        </span>
                    ) : null; })()}
                    {it.invoice_emailed && (
                        <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white border border-emerald-700"
                            data-testid={`disposition-row-invoice-emailed-${it.id}`}
                            title={`Rechnung ${it.invoice_number || ""} per E-Mail an den Kunden gesendet${it.invoice_emailed_at ? " am " + new Date(it.invoice_emailed_at).toLocaleDateString("de-DE") : ""}`}
                        >
                            <Mail className="w-2.5 h-2.5" /> Rechnung gesendet
                        </span>
                    )}
                    {isForeign && (
                        <span
                            className={`inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border ${it.private_seller ? "bg-purple-50 text-purple-700 border-purple-300" : "bg-blue-50 text-blue-700 border-blue-300"}`}
                            data-testid={`disposition-row-foreign-${it.id}`}
                            title={it.private_seller ? "Angebot eines Privatanbieters (B2C) — nur Ansicht" : "Fahrzeug eines anderen Händlers (B2B-sichtbar) — nur Ansicht; Kauf/Reservierung/Vermittlung über die Detailseite"}
                        >
                            <Store className="w-2.5 h-2.5" /> {it.private_seller ? "Privatanbieter" : "Fremder Händler"}{it.owner_company ? ` · ${it.owner_company}` : ""}{it.owner_city ? ` (${it.owner_city})` : ""}
                        </span>
                    )}
                    {foreignFull && (
                        <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border bg-teal-50 text-teal-700 border-teal-300"
                            data-testid={`disposition-row-commission-${it.id}`}
                            title="Dieses fremde Fahrzeug steht an deinem Standort (Kommissionsware) — volle Dispo-Funktionen verfügbar"
                        >
                            <Handshake className="w-2.5 h-2.5" /> Kommission bei dir
                        </span>
                    )}
                    {foreignSold && (
                        <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border bg-green-50 text-green-700 border-green-300"
                            data-testid={`disposition-row-foreign-sold-${it.id}`}
                            title="Von dir verkauft/vermittelt — Auftrag & Transport verfügbar"
                        >
                            Von dir verkauft
                        </span>
                    )}
                    {/* Jul-2026 (User-Wunsch) — Inventur-Zähl-Badges (Fzg/Papiere/Schlüssel)
                        immer in der ERSTEN Zeile, hinter der Angebotsnummer/Status. */}
                    {!isForeign && (
                        <>
                            <LastCountBadge label="Fzg" iso={it.inventory_last_counted_at} testid={`disposition-row-count-vehicle-${it.id}`} />
                            <LastCountBadge label="Papiere" iso={it.papers_inventory_last_counted_at} testid={`disposition-row-count-papers-${it.id}`} />
                            <LastCountBadge label="Schlüssel" iso={it.key_inventory_last_counted_at} testid={`disposition-row-count-key-${it.id}`} />
                        </>
                    )}
                </div>
                <div className="text-[11px] text-gray-600 mt-0.5 flex items-center gap-x-3 gap-y-0.5 flex-wrap">
                    {it.vin
                        ? <span className="font-mono-num inline-flex items-center gap-0.5"><Hash className="w-3 h-3" />VIN {it.vin}
                            {it.vin_duplicate_count > 1 && (
                                <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm border bg-red-50 text-red-700 border-red-300"
                                    title={`Diese FIN ist ${it.vin_duplicate_count}× im Bestand — über „Weitere Funktionen → FIN-Doubletten zusammenführen" bereinigen`}
                                    data-testid={`vin-dupe-badge-${it.id}`}>
                                    FIN-Doublette ×{it.vin_duplicate_count}
                                </span>
                            )}
                        </span>
                        : <VinQuickFix item={it} isForeign={isForeign} />}
                    {it.license_plate && <span className="font-mono-num">Kennzeichen {it.license_plate}</span>}
                    <ActiveUsageBadges item={it} />
                    {it.stand_platz && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{it.stand_platz}</span>}
                    {it.site_code && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded-sm text-[9px] font-bold uppercase tracking-wider text-gray-700" data-testid={`disposition-row-site-${it.id}`}>{it.site_code}</span>}
                    <DeregistrationBadge isoDate={it.last_deregistration_date} vehicle={it} />
                    <VorfuehrUsageBadge item={it} />
                </div>
                {(() => {
                    const ez = it.first_registration_date ? formatEzDate(it.first_registration_date) : (it.year || null);
                    const cleanLoc = (it.location || "")
                        .replace(/,?\s*Deutschland\s*$/i, "")
                        .replace(/,?\s*Germany\s*$/i, "")
                        .trim();
                    const standort = [cleanLoc, it.street].filter(Boolean).join(", ") || null;
                    const sd = it.standtage;
                    const standtageCls = sd == null ? "" : sd >= 120 ? "text-red-600 font-bold" : sd >= 90 ? "text-amber-600 font-bold" : "text-[#111827]";
                    // Jun-2026 (User-Wunsch) — Basisfarbe + Herstellerfarbe (+ Finish)
                    // gemeinsam in einem klaren „Farbe"-Feld anzeigen.
                    const finishLbl = it.color_finish === "matt" ? "matt"
                        : it.color_finish === "glaenzend" ? "glänzend"
                            : it.color_finish === "metallic" ? "metallic" : null;
                    const farbeHersteller = (() => {
                        if (!it.color) return null;
                        return finishLbl ? `${it.color} (${finishLbl})` : it.color;
                    })();
                    // Jul-2026 (User-Wunsch) — feste Feld-Reihenfolge: ALLE Felder
                    // immer an derselben Position. Fehlt ein Wert, steht nur die
                    // Bezeichnung (kein Filtern → identische Ansicht für alle Händler).
                    const fields = [
                        ["Erstzulassung", ez],
                        ["Besitzer", it.previous_owners != null ? it.previous_owners : null],
                        ["KM-Stand", it.mileage != null ? formatMileage(it.mileage) : null],
                        ["Farbbasis", it.color_base || null],
                        ["Farbe Hersteller", farbeHersteller],
                        ["Getriebe", it.transmission || null],
                        ["Gänge", it.gears != null ? it.gears : null],
                        ["Standtage", sd != null ? `${sd} Tage` : null, standtageCls],
                        ["Listenpreis (UVP)", it.list_price_gross ? formatPrice(it.list_price_gross) : null],
                        ["Preis B2C (Privat)", it.price ? formatPrice(it.price) : null],
                        ...(canSeeB2BPrice ? [["Preis B2B (Gewerblich)", it.price_b2b ? formatPrice(it.price_b2b) : null]] : []),
                        ["Standort", standort || null],
                        ["Schlüsseldepot", it.key_depot || null],
                        ["Frühestens", it.earliest_delivery ? formatDeliveryWeek(it.earliest_delivery) : null, "text-emerald-700 font-bold"],
                    ];
                    return (
                        <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-0.5 text-[11px] text-gray-700" data-testid={`disposition-row-fields-${it.id}`}>
                            {fields.map(([label, val, cls]) => {
                                const has = val !== null && val !== undefined && val !== "";
                                return (
                                    <span key={label} className="truncate" data-testid={`disposition-field-${label}-${it.id}`}>
                                        <span className="text-gray-500 font-bold">{label}:</span>
                                        {has ? <> <span className={`font-mono-num ${cls || "text-[#111827]"}`}>{val}</span></> : null}
                                    </span>
                                );
                            })}
                        </div>
                    );
                })()}
                {!isForeign && (
                    <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`disposition-row-commission-note-${it.id}`}>
                        <span className="text-[11px] whitespace-nowrap shrink-0">
                            <span className="text-gray-500 font-bold">Vermittlungsprovision:</span>{" "}
                            <span className="font-mono-num text-[#111827]">{it.referral_commission_eur ? formatPrice(it.referral_commission_eur) : "—"}</span>
                        </span>
                        <DispoRowNote listingId={it.id} initialNote={it.dispo_internal_note || ""} />
                    </div>
                )}
                {!isForeign && isOrderVehicle && (
                    <div className="mt-1.5" data-testid={`disposition-row-order-tracking-${it.id}`}>
                        <button
                            type="button"
                            onClick={() => setShowTracking((v) => !v)}
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-sm bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100"
                            data-testid={`order-tracking-toggle-${it.id}`}
                        >
                            <Truck className="w-3.5 h-3.5" />
                            Bestell-Status: {ORDER_STAGES.find((s) => s.key === it.order_tracking?.status)?.label || "Bestellt"}
                            {it.order_tracking?.eta ? ` · ETA ${new Date(it.order_tracking.eta).toLocaleDateString("de-DE")}` : ""}
                        </button>
                        {showTracking && (
                            <div className="mt-2">
                                <OrderTrackingCard listing={it} onUpdated={(tr) => { it.order_tracking = tr; }} />
                            </div>
                        )}
                    </div>
                )}
                {it.transport && it.transport.status && (
                    <div className="text-[10px] mt-1 text-gray-500 inline-flex items-center gap-1">
                        <Truck className="w-3 h-3" /> Transport: <strong>{it.transport.status}</strong>
                        {it.transport.provider && ` · ${it.transport.provider}`}
                        {it.transport.tracking_number && ` · ${it.transport.tracking_number}`}
                    </div>
                )}
                {it.dispo_progress && (() => {
                    const { done, total } = it.dispo_progress;
                    const pct = total ? Math.round((done / total) * 100) : 0;
                    const bar = pct === 100 ? "bg-emerald-600" : pct >= 50 ? "bg-orange-500" : "bg-red-500";
                    const txt = pct === 100 ? "text-emerald-600" : pct >= 50 ? "text-orange-600" : "text-red-600";
                    const open = it.dispo_progress.open || [];
                    const tip = open.length
                        ? `Offen (${open.length}): ${open.join(", ")}`
                        : "Alle Abwicklungsschritte erledigt ✓";
                    return (
                        <Link
                            to={`/edit-listing/${it.id}?focus=disposition`}
                            className="mt-3 inline-flex items-center gap-2 group"
                            data-testid={`disposition-row-progress-${it.id}`}
                            title={`Abwicklungs-Fortschritt: ${done}/${total} Schritte · ${tip} — klicken zum Öffnen`}
                        >
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 group-hover:text-[#111827] transition-colors">Abwicklung</span>
                            <div className="h-1.5 w-24 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                                <div className={`h-full ${bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-[10px] font-bold ${txt}`} data-testid={`disposition-row-progress-count-${it.id}`}>
                                {done}/{total} Schritte
                            </span>
                        </Link>
                    );
                })()}

                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`disposition-row-actions-${it.id}`}>
                    {/* Jul-2026 (User-Wunsch) — Foto-Button links: geführte Fotos + Video direkt am Fahrzeug */}
                    <button
                        type="button"
                        onClick={() => setShowPhotoCapture(true)}
                        className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-[#FFCC00] border border-[#111827] text-[#111827] hover:bg-amber-300 transition-colors"
                        data-testid={`disposition-row-photo-${it.id}`}
                        title="Fahrzeugfotos mit Positions-Vorgabe (Tacho, vorne, hinten …) und optionalem Video direkt aufnehmen und am Fahrzeug speichern">
                        <CameraIcon className="w-3 h-3" /> Foto
                    </button>
                    {showPhotoCapture && (
                        <DispoPhotoCaptureModal item={it} open={showPhotoCapture} onOpenChange={setShowPhotoCapture} />
                    )}
                    {isForeign && !foreignFull ? (
                        <>
                            <Link
                                to={`/listing/${it.id}`}
                                className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm border border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] transition-colors"
                                data-testid={`disposition-row-foreign-view-${it.id}`}
                                target="_blank" rel="noopener noreferrer"
                                title="Fahrzeug ansehen">
                                <Eye className="w-3 h-3" /> Ansehen
                            </Link>
                            {!it.sold && (
                                <button
                                    type="button"
                                    onClick={() => onCreateOffer?.(it)}
                                    className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-blue-700 text-white hover:bg-blue-800 transition-colors"
                                    data-testid={`disposition-row-offer-${it.id}`}
                                    title="Repräsentatives Angebot (PDF mit QR) per E-Mail oder WhatsApp an einen Kunden senden"
                                >
                                    <Mail className="w-3 h-3" /> Angebot
                                </button>
                            )}
                            {!saleBlocked && (
                                <button
                                    type="button"
                                    onClick={() => onForeignReserve?.(it)}
                                    className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                                    data-testid={`disposition-row-foreign-reserve-${it.id}`}
                                    title="Dieses fremde Fahrzeug direkt für 48h reservieren (B2B)">
                                    <Clock className="w-3 h-3" /> B2B reservieren
                                </button>
                            )}
                            {!saleBlocked && (
                                <button
                                    type="button"
                                    onClick={() => onForeignDeal?.(it)}
                                    className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-green-700 text-white hover:bg-green-800 transition-colors"
                                    data-testid={`disposition-row-foreign-deal-${it.id}`}
                                    title="Kaufen (selbst) oder an einen Endkunden vermitteln">
                                    <Handshake className="w-3 h-3" /> Verkauf / Vermittlung
                                </button>
                            )}
                            {foreignSold && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => onCreateDelivery?.(it)}
                                        className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-white border border-[#111827] text-[#111827] hover:bg-gray-50 transition-colors"
                                        data-testid={`disposition-row-delivery-order-${it.id}`}
                                        title="Auslieferungs-/Vorbereitungsauftrag (Werkstatt) für das vermittelte Fahrzeug anlegen"
                                    >
                                        <ClipboardCheck className="w-3 h-3" /> Auftrag
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onCreateTransport?.(it)}
                                        className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-indigo-700 text-white hover:bg-indigo-800 transition-colors"
                                        data-testid={`disposition-row-transport-order-${it.id}`}
                                        title="Transportauftrag für das vermittelte Fahrzeug erstellen"
                                    >
                                        <Truck className="w-3 h-3" /> Transport
                                    </button>
                                </>
                            )}
                        </>
                    ) : (
                    <>
                    <Link
                        to={`/edit-listing/${it.id}?focus=disposition`}
                        className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm border border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] transition-colors"
                        data-testid={`disposition-row-open-${it.id}`}
                        target="_blank" rel="noopener noreferrer">
                        Dispo öffnen <ArrowRight className="w-3 h-3" />
                    </Link>
                    {/* Aug-2026 (User-Wunsch) — DMS-Archiv: öffnet das e-Archivsystem mit der FIN des Fahrzeugs */}
                    {it.vin && (
                        <a
                            href={dmsUrlForVin(dmsBase, it.vin)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-teal-700 text-white hover:bg-teal-800 transition-colors"
                            data-testid={`disposition-row-dms-${it.id}`}
                            title={`Dokumenten-Archiv (DMS) zu diesem Fahrzeug öffnen — FIN ${it.vin}`}
                            onClick={async () => {
                                // Aug-2026 (User-Wunsch): Pietsch-Login hat keine URL-Parameter →
                                // Passwort automatisch in die Zwischenablage kopieren (2-Sekunden-Login).
                                try {
                                    const s = await getDmsSettings();
                                    if (s?.password) {
                                        await navigator.clipboard.writeText(s.password);
                                        toast.info(`DMS-Login: ${s.username || "Benutzer"} — Passwort wurde in die Zwischenablage kopiert, einfach einfügen.`, { duration: 8000 });
                                    }
                                } catch { /* Zwischenablage nicht verfügbar — Link öffnet trotzdem */ }
                            }}
                        >
                            <Archive className="w-3 h-3" /> DMS
                        </a>
                    )}
                    {!it.sold && (
                        <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/listings/${it.id}/price-tag.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-white border border-[#111827] text-[#111827] hover:bg-gray-50 transition-colors"
                            data-testid={`disposition-row-pricetag-${it.id}`}
                            title="Druckbares A5-Preisschild mit Händlerlogo, Kontaktdaten & QR-Code öffnen"
                        >
                            <Printer className="w-3 h-3" /> Preisschild
                        </a>
                    )}
                    {!it.sold && (
                        <a
                            href={currentStandort ? `/listing/${it.id}/qrcard?loc=${encodeURIComponent(currentStandort)}` : `/listing/${it.id}/qrcard`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-white border border-[#111827] text-[#111827] hover:bg-gray-50 transition-colors"
                            data-testid={`disposition-row-qrcard-${it.id}`}
                            title={currentStandort ? `Druckbare QR-Karten — aktueller Standort „${currentStandort}" wird übernommen` : "Druckbare QR-Karten (Schlüssel · Vorne Links · Hinten Rechts)"}
                        >
                            <QrCode className="w-3 h-3" /> QR Code
                        </a>
                    )}
                    {!it.sold && (
                        <button
                            type="button"
                            onClick={() => onTestDrive?.(it)}
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-[#111827] text-white hover:bg-black transition-colors"
                            data-testid={`disposition-row-testdrive-${it.id}`}
                            title="Probefahrt-Vereinbarung mit Kunden- und Fahrzeugdaten erstellen (PDF) — wird als Lead zur Nachverfolgung übernommen"
                        >
                            <Car className="w-3 h-3" /> Probefahrt
                        </button>
                    )}
                    {!saleBlocked && onSofortmitnahme && (
                        <button
                            type="button"
                            onClick={() => onSofortmitnahme?.(it)}
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 transition-colors"
                            data-testid={`disposition-row-sofortmitnahme-${it.id}`}
                            title="Sofortmitnahme-Check: prüft je nach Hubraum alle Mitnahme-Voraussetzungen und holt die Filialleiter-Freigabe ein"
                        >
                            <Zap className="w-3 h-3" /> Sofortmitnahme
                        </button>
                    )}
                    {!it.sold && (
                        <button
                            type="button"
                            onClick={() => onCreateOffer?.(it)}
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-blue-700 text-white hover:bg-blue-800 transition-colors"
                            data-testid={`disposition-row-offer-${it.id}`}
                            title="Repräsentatives Angebot (PDF mit QR) per E-Mail oder WhatsApp an einen Kunden senden"
                        >
                            <Mail className="w-3 h-3" /> Angebot
                        </button>
                    )}
                    {/* Jul-2026 (Bugfix, User-Meldung): Button nur bei wirklich
                        VERFÜGBAREN Fahrzeugen — bei verkauft/reserviert/laufendem
                        Verkaufsprozess ausgeblendet; erscheint automatisch wieder,
                        wenn die Reservierung abläuft oder der Verkauf storniert wird. */}
                    {!it.sold && !isForeign && !["verkauft", "reserviert", "vorläufig verkauft"].includes(it.status_label) && (
                        <Link
                            to={`/edit-listing/${it.id}?focus=disposition&action=sell`}
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-green-700 text-white hover:bg-green-800 transition-colors"
                            data-testid={`disposition-row-sell-${it.id}`}
                            target="_blank" rel="noopener noreferrer">
                            <ShoppingCart className="w-3 h-3" /> Verkaufen / Reservieren
                        </Link>
                    )}
                    {!saleBlocked && isForeign && (
                        <button
                            type="button"
                            onClick={() => onForeignDeal?.(it)}
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-green-700 text-white hover:bg-green-800 transition-colors"
                            data-testid={`disposition-row-foreign-deal-${it.id}`}
                            title="Kommissionsfahrzeug: Kaufen (selbst) oder an einen Endkunden vermitteln">
                            <Handshake className="w-3 h-3" /> Verkauf / Vermittlung
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => onCreateDelivery?.(it)}
                        className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-white border border-[#111827] text-[#111827] hover:bg-gray-50 transition-colors"
                        data-testid={`disposition-row-delivery-order-${it.id}`}
                        title="Internen Auslieferungs-/Vorbereitungsauftrag (Werkstatt) mit auszuführenden Arbeiten anlegen"
                    >
                        <ClipboardCheck className="w-3 h-3" /> Auftrag
                    </button>
                    <button
                        type="button"
                        onClick={() => onCreateTransport?.(it)}
                        className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-indigo-700 text-white hover:bg-indigo-800 transition-colors"
                        data-testid={`disposition-row-transport-order-${it.id}`}
                        title="Transportauftrag für dieses Fahrzeug erstellen (Transportplanung)"
                    >
                        <Truck className="w-3 h-3" /> Transport
                    </button>
                    {!it.sold && !isForeign && it.standtage != null && it.standtage > 120 && (
                        <button
                            type="button"
                            onClick={() => onLadenhueterAction(it)}
                            className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm bg-red-600 text-white hover:bg-red-700 transition-colors"
                            data-testid={`disposition-row-ladenhueter-action-${it.id}`}
                        >
                            <TrendingDown className="w-3 h-3" /> Preis senken / boosten
                        </button>
                    )}
                    {/* Jul-2026 (User-Wunsch) — Käufer & Verkäufer rechts neben den Aktions-Buttons. */}
                    {((it.buyer && (it.buyer.first_name || it.buyer.last_name || it.buyer.email)) || it.seller?.name) && (
                        <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid={`disposition-row-parties-${it.id}`}>
                            {it.buyer && (it.buyer.first_name || it.buyer.last_name || it.buyer.email) && (
                                <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-sm bg-amber-50 border border-amber-300 text-amber-900" title="Käufer" data-testid={`disposition-row-buyer-${it.id}`}>
                                    <strong>Käufer:</strong>
                                    <span>{[it.buyer.first_name, it.buyer.last_name].filter(Boolean).join(" ") || it.buyer.email}</span>
                                    {it.buyer.phone && (
                                        <a href={`tel:${it.buyer.phone}`} className="inline-flex items-center text-blue-700 hover:underline" title={it.buyer.phone}><Phone className="w-3 h-3" /></a>
                                    )}
                                    {it.buyer.email && (
                                        <a href={`mailto:${it.buyer.email}`} className="inline-flex items-center text-blue-700 hover:underline" title={it.buyer.email}><Mail className="w-3 h-3" /></a>
                                    )}
                                </span>
                            )}
                            {it.seller?.name && (
                                <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-sm bg-sky-50 border border-sky-300 text-sky-900" title="Verkäufer" data-testid={`disposition-row-seller-${it.id}`}>
                                    <strong>Verkäufer:</strong>
                                    <span>{it.seller.name}</span>
                                    {it.sale_date && (
                                        <span className="text-sky-700" title="Verkaufsdatum" data-testid={`disposition-row-sale-date-${it.id}`}>
                                            · {new Date(it.sale_date).toLocaleDateString("de-DE")}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        </li>
    );
}

